import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const generatePDFFromHTML = async (element, fileName = 'power-plant-offer.pdf', offerData) => {
  try {
    // Show loading indicator
    const loadingDiv = document.createElement('div');
    loadingDiv.innerHTML = 'Generating PDF...';
    loadingDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.8);
      color: white;
      padding: 20px 40px;
      border-radius: 8px;
      font-size: 18px;
      z-index: 9999;
    `;
    document.body.appendChild(loadingDiv);

    // Use the advanced PDF generator for better control
    await generateAdvancedPDF(offerData);

    // Remove loading indicator
    document.body.removeChild(loadingDiv);

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    
    // Remove loading indicator if it exists
    const loadingDiv = document.querySelector('div[style*="Generating PDF"]');
    if (loadingDiv) {
      document.body.removeChild(loadingDiv);
    }
    
    alert('Error generating PDF. Please try again.');
    return false;
  }
};

export const generateAdvancedPDF = async (offerData) => {
  // --- Helpers for image orientation and sizing ---
  const fetchBlob = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch image');
    return await res.blob();
  };

  const readExifOrientation = async (blob) => {
    try {
      const buffer = await blob.arrayBuffer();
      const view = new DataView(buffer);
      if (view.getUint16(0, false) !== 0xFFD8) return 1; // not JPEG
      let offset = 2;
      const length = view.byteLength;
      while (offset < length) {
        const marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xFFE1) { // APP1
          const exifLength = view.getUint16(offset, false);
          offset += 2;
          if (view.getUint32(offset, false) !== 0x45786966) return 1; // 'Exif'
          offset += 6; // skip 'Exif\0\0'
          const little = view.getUint16(offset, false) === 0x4949;
          offset += view.getUint32(offset + 4, little);
          const tags = view.getUint16(offset, little);
          offset += 2;
          for (let i = 0; i < tags; i++) {
            const tagOffset = offset + i * 12;
            const tag = view.getUint16(tagOffset, little);
            if (tag === 0x0112) {
              return view.getUint16(tagOffset + 8, little) || 1;
            }
          }
          break;
        } else if ((marker & 0xFF00) !== 0xFF00) {
          break;
        } else {
          offset += view.getUint16(offset, false);
        }
      }
    } catch (e) { /* ignore */ }
    return 1;
  };

  // Use high DPI for crisper canvas generation
  const mmToPx = (mm, dpi = 300) => Math.max(1, Math.round(mm * (dpi / 25.4)));

  // Approximate decoded byte size of a base64 data URL
  const dataUrlBytes = (u) => Math.ceil(((u.length - (u.indexOf(',') + 1)) * 3) / 4);

  // Returns a dataURL sized to fit pixelMaxW/pixelMaxH - NO ROTATION.
  // If maxBytes is given, the encoder targets that byte budget the way
  // Acrobat's "reduce file size" does: step JPEG quality down first, and
  // only if quality alone isn't enough, progressively reduce resolution.
  const getResizedDataUrl = async (url, pixelMaxW, pixelMaxH, maxBytes = Infinity) => {
    const blob = await fetchBlob(url);
    
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.src = objectUrl;
    await new Promise((r) => { img.onload = r; img.onerror = r; });

    // Calculate target dimensions
    const ratio = Math.min(pixelMaxW / (img.width || 1), pixelMaxH / (img.height || 1), 1);
    let targetW = Math.max(1, Math.round((img.width || 1) * ratio));
    let targetH = Math.max(1, Math.round((img.height || 1) * ratio));

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Enable high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // When shrinking by more than 2x, downscale in two steps (half size first)
    // to avoid aliasing artifacts from a single large downscale.
    let source = img;
    if (ratio < 0.5) {
      const midW = Math.max(targetW, Math.round((img.width || 1) / 2));
      const midH = Math.max(targetH, Math.round((img.height || 1) / 2));
      const midCanvas = document.createElement('canvas');
      midCanvas.width = midW;
      midCanvas.height = midH;
      const midCtx = midCanvas.getContext('2d');
      midCtx.imageSmoothingEnabled = true;
      midCtx.imageSmoothingQuality = 'high';
      midCtx.drawImage(img, 0, 0, midW, midH);
      source = midCanvas;
    }

    // NO ROTATION - just draw the image as is
    canvas.width = targetW;
    canvas.height = targetH;
    ctx.drawImage(source, 0, 0, targetW, targetH);

    URL.revokeObjectURL(objectUrl);
    
    // 0.82 is visually indistinguishable from 0.95 for photos but ~2-3x smaller
    let quality = 0.82;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    
    // Step quality down (never below 0.5) until the byte budget is met
    const qualityFloor = 0.5;
    while (dataUrlBytes(dataUrl) > maxBytes && quality > qualityFloor) {
      quality = Math.max(qualityFloor, quality - 0.08);
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    
    // If quality alone isn't enough, shrink resolution in 15% steps,
    // but never below ~700 px on the long edge to keep photos legible
    let workCanvas = canvas;
    while (dataUrlBytes(dataUrl) > maxBytes && Math.max(workCanvas.width, workCanvas.height) > 700) {
      const next = document.createElement('canvas');
      next.width = Math.max(1, Math.round(workCanvas.width * 0.85));
      next.height = Math.max(1, Math.round(workCanvas.height * 0.85));
      const nextCtx = next.getContext('2d');
      nextCtx.imageSmoothingEnabled = true;
      nextCtx.imageSmoothingQuality = 'high';
      nextCtx.drawImage(workCanvas, 0, 0, next.width, next.height);
      workCanvas = next;
      dataUrl = workCanvas.toDataURL('image/jpeg', quality);
    }
    
    return dataUrl;
  };
  
  // Detect whether an image source is a PNG. Works for both inline data URLs
  // (data:image/png;base64,...) AND remote URLs (e.g. Supabase storage URLs
  // ending in .png) — important because a logo/watermark that's been saved is
  // reloaded as a URL, not a data URL, so a plain `startsWith('data:image/png')`
  // check would always fail for it.
  const isPngLogo = (src) => {
    if (!src || typeof src !== 'string') return false;
    if (src.startsWith('data:image/png')) return true;
    // Strip query string / fragment, then check extension
    const noQuery = src.split(/[?#]/)[0];
    return /\.png$/i.test(noQuery);
  };

  // Get original image dimensions without loading full image data
  const getImageDimensions = async (url) => {
    const blob = await fetchBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.src = objectUrl;
    await new Promise((r) => { img.onload = r; img.onerror = r; });
    const dimensions = { width: img.width || 1, height: img.height || 1 };
    URL.revokeObjectURL(objectUrl);
    return dimensions;
  };

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (2 * margin);
  const footerHeight = 25;
  const contentHeight = pageHeight - margin - footerHeight - 10;

  // Watermark is processed once and cached so every page reuses the exact
  // same data URL string — jsPDF then deduplicates it into a single embedded
  // image object instead of embedding a copy per page.
  let watermarkCache = null;

  // Helper function to add watermark to current page
  const addWatermark = async () => {
    if (offerData.watermarkImage || offerData.logoImage) {
      const watermarkImage = offerData.watermarkImage || offerData.logoImage;
      
      try {
        if (!watermarkCache) {
          // Create temporary image to get dimensions. crossOrigin lets us
          // draw remote images (e.g. Supabase storage) onto a canvas without
          // tainting it, so they can be re-encoded like data-URL images.
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = watermarkImage;
          
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
          
          let watermarkWidth, watermarkHeight;
          
          if (img.width && img.height) {
            // Calculate aspect ratio
            const aspectRatio = img.width / img.height;
            
            // Calculate maximum size - large but not overwhelming
            const maxSize = Math.min(pageWidth * 0.7, pageHeight * 0.5);
            
            // Scale proportionally
            if (aspectRatio >= 1) {
              // Wider than tall
              watermarkWidth = maxSize;
              watermarkHeight = maxSize / aspectRatio;
            } else {
              // Taller than wide
              watermarkHeight = maxSize;
              watermarkWidth = maxSize * aspectRatio;
            }
          } else {
            // Fallback to square if dimensions unknown
            watermarkWidth = watermarkHeight = Math.min(pageWidth * 0.7, pageHeight * 0.5);
          }
          
          // Center the watermark
          const watermarkX = (pageWidth - watermarkWidth) / 2;
          const watermarkY = (pageHeight - watermarkHeight) / 2;
          
          // Determine image format — handles both data URLs and .png file URLs.
          // A plain startsWith('data:image/png') check misclassified PNG
          // watermarks loaded from storage URLs as JPEG, which flattened their
          // transparent background to black (rendered as a gray box on pages).
          const isPNG = isPngLogo(watermarkImage);
          
          let processedImage = null;
          if (isPNG) {
            // For PNG images, we need to process them through canvas to handle transparency correctly.
            // Cap the canvas at ~1200px on the long edge — the watermark is only
            // drawn at ~70% of the page width, so full source resolution is wasted.
            const maxDim = 1200;
            const srcW = img.width || 1;
            const srcH = img.height || 1;
            const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(srcW * scale));
            canvas.height = Math.max(1, Math.round(srcH * scale));
            const ctx = canvas.getContext('2d', { alpha: true });
            
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Fill with white background first
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Set global alpha for transparency effect
            ctx.globalAlpha = 0.15;
            
            // Draw the image
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            // Convert to JPEG to avoid transparency issues. Quality 0.8 is more
            // than enough — the watermark is drawn at 15% opacity.
            processedImage = canvas.toDataURL('image/jpeg', 0.8);
          } else {
            // Non-PNG watermarks were embedded at original resolution — a
            // phone-camera JPEG used as watermark could be several MB on its
            // own. Cap it at 1200px on the long edge too. Opacity is applied
            // via GState at draw time, so we draw at full alpha here.
            try {
              const maxDim = 1200;
              const srcW = img.width || 1;
              const srcH = img.height || 1;
              const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
              const canvas = document.createElement('canvas');
              canvas.width = Math.max(1, Math.round(srcW * scale));
              canvas.height = Math.max(1, Math.round(srcH * scale));
              const ctx = canvas.getContext('2d');
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              // Fill white first: if the source has transparency after all
              // (e.g. a WebP or a PNG we failed to detect), JPEG encoding
              // would otherwise turn transparent pixels black.
              ctx.fillStyle = 'white';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              processedImage = canvas.toDataURL('image/jpeg', 0.8);
            } catch (e) {
              // Tainted canvas (cross-origin watermark without CORS headers) —
              // fall back to embedding the original image.
              processedImage = null;
            }
          }
          
          watermarkCache = { isPNG, processedImage, watermarkX, watermarkY, watermarkWidth, watermarkHeight };
        }
        
        const { isPNG, processedImage, watermarkX, watermarkY, watermarkWidth, watermarkHeight } = watermarkCache;
        
        if (isPNG) {
          // Add as JPEG (same cached data URL every page → deduplicated by jsPDF)
          pdf.addImage(
            processedImage,
            'JPEG',
            watermarkX,
            watermarkY,
            watermarkWidth,
            watermarkHeight,
            undefined,
            'FAST'
          );
        } else {
          // For non-PNG images, use opacity
          pdf.saveGraphicsState();
          
          const gState = new pdf.GState();
          gState.opacity = 0.15;
          pdf.setGState(gState);
          
          pdf.addImage(
            processedImage || watermarkImage,
            'JPEG',
            watermarkX,
            watermarkY,
            watermarkWidth,
            watermarkHeight,
            undefined,
            'FAST'
          );
          
          pdf.restoreGraphicsState();
        }
      } catch (error) {
        console.error('Failed to add watermark:', error);
      }
    } else {
      // Text watermark fallback
      pdf.saveGraphicsState();
      
      const gState = new pdf.GState();
      gState.opacity = 0.2;
      pdf.setGState(gState);
      
      // Large centered text
      pdf.setFontSize(60);
      pdf.setTextColor(43, 62, 92);
      
      // Rotate text
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;
      
      // Save current transformation matrix
      pdf.saveGraphicsState();
      
      // Translate to center, rotate, then translate back
      pdf.internal.write('1 0 0 1 ' + centerX + ' ' + centerY + ' cm'); // translate to center
      pdf.internal.write('0.866 -0.5 0.5 0.866 0 0 cm'); // rotate 30 degrees
      pdf.internal.write('1 0 0 1 ' + (-centerX) + ' ' + (-centerY) + ' cm'); // translate back
      
      pdf.text('ATLANTIS', centerX, centerY, { align: 'center' });
      
      pdf.restoreGraphicsState();
      pdf.restoreGraphicsState();
    }
  };

  // Helper function to add footer to current page
  const addFooter = () => {
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    
    // Draw line above footer
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, pageHeight - footerHeight - 5, pageWidth - margin, pageHeight - footerHeight - 5);
    
    // Add company details
    pdf.setFont(undefined, 'bold');
    pdf.text(offerData.companyDetails.name || 'Company Name', pageWidth / 2, pageHeight - footerHeight + 2, { align: 'center' });
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8);
    pdf.text(
      `${offerData.companyDetails.addressLine1} | ${offerData.companyDetails.addressLine2} | ${offerData.companyDetails.addressLine3}`,
      pageWidth / 2, 
      pageHeight - footerHeight + 7, 
      { align: 'center' }
    );
    
    // Add email if available
    if (offerData.companyDetails.email) {
      pdf.text(offerData.companyDetails.email, pageWidth / 2, pageHeight - footerHeight + 12, { align: 'center' });
    }
  };

  // Helper function to add new page with watermark and footer
  const addNewPage = async (withWatermark = true) => {
    pdf.addPage();
    if (withWatermark) {
      await addWatermark();
    }
    addFooter();
  };

  // Helper function to wrap long titles
  const wrapTitle = (text, x, y, options = {}) => {
    const { align = 'left', maxWidth = pageWidth - 40, lineHeight = 10 } = options;
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const textWidth = pdf.getTextWidth(testLine);
      
      if (textWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    // Render the lines
    const startY = lines.length > 1 && align === 'center' 
      ? y - (lineHeight * (lines.length - 1) / 2) 
      : y;
    
    lines.forEach((line, index) => {
      const lineY = startY + (index * lineHeight);
      if (align === 'center') {
        pdf.text(line, x, lineY, { align: 'center' });
      } else {
        pdf.text(line, x, lineY);
      }
    });
    
    return lines.length * lineHeight;
  };

  // Helper function to add text with wrapping
  const addWrappedText = (text, x, y, fontSize = 12, isBold = false, maxWidth = contentWidth) => {
    pdf.setFontSize(fontSize);
    if (isBold) pdf.setFont(undefined, 'bold');
    else pdf.setFont(undefined, 'normal');
    
    const lines = pdf.splitTextToSize(text, maxWidth);
    const lineHeight = fontSize * 0.5;
    
    lines.forEach((line, index) => {
      pdf.text(line, x, y + (index * lineHeight));
    });
    
    return lines.length * lineHeight;
  };

  // Page 1: Cover Page
  pdf.setFillColor(250, 250, 250);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // No watermark on cover page
  
  // Add logo if available with proper aspect ratio
  let logoY = 40;
  if (offerData.logoImage) {
    try {
      // Create temporary image to get dimensions
      const img = new Image();
      // Allow cross-origin images (e.g. Supabase storage URLs) to load with
      // dimensions correctly. Without this, img.width/height can be 0.
      img.crossOrigin = 'anonymous';
      img.src = offerData.logoImage;
      
      // Wait for image to load to get dimensions
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Continue even if error
      });
      
      const isLogoPNG = isPngLogo(offerData.logoImage);
      const logoFormat = isLogoPNG ? 'PNG' : 'JPEG';

      if (img.width && img.height) {
        // Calculate aspect ratio
        const aspectRatio = img.width / img.height;
        
        // Set maximum dimensions
        const maxWidth = 80;
        const maxHeight = 60;
        
        let logoWidth, logoHeight;
        
        // Scale proportionally to fit within max dimensions
        if (aspectRatio > maxWidth / maxHeight) {
          // Image is wider than max aspect ratio
          logoWidth = maxWidth;
          logoHeight = maxWidth / aspectRatio;
        } else {
          // Image is taller than max aspect ratio
          logoHeight = maxHeight;
          logoWidth = maxHeight * aspectRatio;
        }
        
        // Center horizontally
        const logoX = (pageWidth - logoWidth) / 2;
        
        // Downscale the logo to at most 2x its rendered size at 150 DPI —
        // some logo files are several megapixels / over 1 MB, which is wasted
        // at the small size it's drawn in the PDF. PNG logos stay PNG so
        // transparency is preserved (no white background, no JPEG flattening).
        let logoSrc = offerData.logoImage;
        try {
          const maxPxW = mmToPx(logoWidth, 150) * 2;
          const maxPxH = mmToPx(logoHeight, 150) * 2;
          const logoScale = Math.min(maxPxW / img.width, maxPxH / img.height, 1);
          if (logoScale < 1) {
            const logoCanvas = document.createElement('canvas');
            logoCanvas.width = Math.max(1, Math.round(img.width * logoScale));
            logoCanvas.height = Math.max(1, Math.round(img.height * logoScale));
            const logoCtx = logoCanvas.getContext('2d', { alpha: true });
            logoCtx.imageSmoothingEnabled = true;
            logoCtx.imageSmoothingQuality = 'high';
            logoCtx.drawImage(img, 0, 0, logoCanvas.width, logoCanvas.height);
            logoSrc = isLogoPNG
              ? logoCanvas.toDataURL('image/png')
              : logoCanvas.toDataURL('image/jpeg', 0.85);
          }
        } catch (e) {
          // Canvas can be tainted for cross-origin images without CORS headers;
          // fall back to embedding the original logo.
          logoSrc = offerData.logoImage;
        }
        
        // Add the logo. For PNGs we never paint a white rectangle behind it
        // — that's exactly what produced the "copy-pasted on the page" look.
        // For JPEGs (which can't be transparent anyway) we also skip the
        // white box so the logo blends with the page background uniformly.
        pdf.addImage(logoSrc, logoFormat, logoX, logoY, logoWidth, logoHeight, undefined, 'FAST');
        logoY = logoY + logoHeight + 20;
      } else {
        // Fallback if dimensions couldn't be determined
        const logoMaxWidth = 80;
        const logoX = (pageWidth - logoMaxWidth) / 2;
        pdf.addImage(offerData.logoImage, logoFormat, logoX, logoY, logoMaxWidth, 60, undefined, 'FAST');
        logoY = 110;
      }
    } catch (error) {
      console.error('Failed to add logo:', error);
      logoY = 40; // Reset if logo fails
    }
  }

  // Center content vertically on cover page
  const coverContentY = pageHeight / 2 - 40;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(28);
  pdf.setFont(undefined, 'bold');
  
  // Main headline from plant specs
  const headline = `${offerData.plantCapacity || 'XX MW'} ${offerData.plantTitle || 'Power Plant'}`;
  wrapTitle(headline, pageWidth / 2, coverContentY, { align: 'center', lineHeight: 10 });
  
  pdf.setFontSize(20);
  pdf.setFont(undefined, 'normal');
  const plantFuelType = offerData.fuelType || 'Gas';
  pdf.text(`Complete ${plantFuelType} Power Plant`, pageWidth / 2, coverContentY + 15, { align: 'center' });
  
  // Quotation number
  pdf.setFontSize(16);
  pdf.setTextColor(50, 50, 50);
  pdf.text(`Quotation No: ${offerData.quotationNo}`, pageWidth / 2, coverContentY + 35, { align: 'center' });
  
  // Date
  pdf.setFontSize(14);
  pdf.text(offerData.date, pageWidth / 2, coverContentY + 45, { align: 'center' });
  
  // Attention of
  pdf.setFontSize(14);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Attention of: ${offerData.clientName || 'Client Name'}`, pageWidth / 2, coverContentY + 60, { align: 'center' });
  
  // Add footer to cover page
  addFooter();

  // Page 2: Executive Summary / Subject Letter
  await addNewPage(true); // Add watermark to this page
  let yPosition = margin + 10;
  
  pdf.setTextColor(0, 0, 0);
  
  // Client details box
  pdf.setFillColor(248, 248, 248);
  pdf.rect(margin, yPosition, contentWidth, 35, 'F');
  pdf.setDrawColor(200, 200, 200);
  pdf.rect(margin, yPosition, contentWidth, 35, 'S');
  
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'bold');
  pdf.text('To:', margin + 5, yPosition + 8);
  pdf.setFont(undefined, 'normal');
  pdf.text(offerData.clientName || 'Client Name', margin + 5, yPosition + 15);
  
  const addressLines = pdf.splitTextToSize(offerData.clientAddress || 'Client Address', contentWidth - 10);
  addressLines.forEach((line, index) => {
    pdf.text(line, margin + 5, yPosition + 22 + (index * 5));
  });
  
  yPosition += 45;
  
  // Subject
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(14);
  pdf.text('Subject:', margin, yPosition);
  pdf.setFont(undefined, 'normal');
  
  const subjectText = `Quotation of ${offerData.plantCapacity || 'XX MW'} - ${offerData.plantTitle || 'Power Plant'}`;
  const titleHeight = wrapTitle(subjectText, margin, yPosition + 8, { maxWidth: pageWidth - 2 * margin, lineHeight: 6 });
  
  yPosition += Math.max(20, titleHeight + 12); // Adjust spacing based on title height
  
  // Dear Sir letter
  pdf.setFontSize(12);
  addWrappedText('Dear Sir,', margin, yPosition);
  yPosition += 10;
  
  const introText = `Thank you for your enquiry of this ${offerData.plantCapacity || 'XX MW'} ${(offerData.fuelType || 'gas').toLowerCase()} power plant for your project. We are glad to propose the below for your consideration.`;
  yPosition += addWrappedText(introText, margin, yPosition);
  
  yPosition += 15;
  
  // Plant Specifications
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(14);
  pdf.text('Plant Specifications:', margin, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'normal');
  
  // Helper to add units if not present
  const addUnit = (value, unit) => {
    if (!value) return '';
    const str = String(value).trim();
    // Check if unit is already present (case-insensitive)
    const unitRegex = new RegExp(`\\s*${unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
    if (unitRegex.test(str)) {
      return str; // Unit already present
    }
    return `${str} ${unit}`;
  };
  
  // Plant-level specs
  const plantSpecs = [
    ['Plant Title', offerData.plantTitle],
    ['Capacity', offerData.plantCapacity ? addUnit(offerData.plantCapacity, 'MW') : ''],
    ['Fuel Type', offerData.fuelType]
  ].filter(spec => spec[1]);
  
  // Calculate total height needed
  const plantSpecsHeight = plantSpecs.length * 7 + 5;
  
  // Check if we have enough space for all plant specs
  if (yPosition + plantSpecsHeight > contentHeight) {
    addNewPage();
    yPosition = margin + 10;
  }
  
  for (let i = 0; i < plantSpecs.length; i++) {
    const spec = plantSpecs[i];
    pdf.setFont(undefined, 'normal');
    pdf.text(spec[0] + ':', margin, yPosition);
    pdf.setFont(undefined, 'bold');
    pdf.text(spec[1], margin + 50, yPosition);
    yPosition += 7;
  }
  
  yPosition += 10;
  
  // Equipment details - handle both new array format and old single format
  if (offerData.equipment && Array.isArray(offerData.equipment) && offerData.equipment.length > 0) {
    // New format with multiple equipment - use table layout with blue headers
    for (let eqIndex = 0; eqIndex < offerData.equipment.length; eqIndex++) {
      const eq = offerData.equipment[eqIndex];
      
      const equipmentSpecs = [
        ['Equipment Type', eq.equipmentType],
        ['Quantity', eq.quantity ? `${eq.quantity} ${eq.quantity === '1' ? 'Unit' : 'Units'}` : ''],
        ['Manufacturer', eq.engineManufacturer],
        ['Model/Type', eq.engineType],
        ['Output', eq.engineOutput ? addUnit(eq.engineOutput, 'KW') : ''],
        ['Year Built', eq.yearBuilt],
        ['Working Hours', eq.workingHours ? addUnit(eq.workingHours, 'Hours') : ''],
        ['Alternator/Generator', eq.alternatorType],
        ['Voltage', eq.voltage ? addUnit(eq.voltage, 'KV') : ''],
        ['Frequency', eq.frequency ? addUnit(eq.frequency, 'Hz') : ''],
        ['Power Rating', eq.powerRating ? addUnit(eq.powerRating, 'KVa') : ''],
        ['Speed', eq.speed ? addUnit(eq.speed, 'rpm') : '']
      ].filter(spec => spec[1]);
      
      // Calculate space needed: blue header (10) + table rows
      const headerHeight = 10;
      const tableHeight = equipmentSpecs.length * 8;
      const totalNeeded = headerHeight + tableHeight + 15;
      
      // If not enough space for entire equipment section, start new page
      if (yPosition + totalNeeded > contentHeight) {
        await addNewPage();
        yPosition = margin + 10;
      }
      
      // Blue header box with equipment title
      const equipmentTitle = offerData.equipment.length > 1 
        ? `Equipment ${eqIndex + 1}: ${eq.quantity && eq.quantity !== '1' ? `${eq.quantity} x ` : ''}${eq.equipmentType || 'Equipment'}`
        : 'Equipment Details';
      
      pdf.setFillColor(43, 62, 92);  // Dark blue matching other sections
      pdf.rect(margin, yPosition, contentWidth, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text(equipmentTitle, margin + 5, yPosition + 7);
      
      yPosition += 15;
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      
      // Table with equipment specifications
      const labelWidth = 60;
      const valueWidth = contentWidth - labelWidth;
      
      for (let specIndex = 0; specIndex < equipmentSpecs.length; specIndex++) {
        const spec = equipmentSpecs[specIndex];
        const rowHeight = 8;
        
        // Check if we need a new page
        if (yPosition + rowHeight > contentHeight) {
          await addNewPage();
          yPosition = margin + 10;
        }
        
        // Alternating row colors
        if (specIndex % 2 === 0) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(margin, yPosition - 5, contentWidth, rowHeight, 'F');
        }
        
        // Draw borders
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.1);
        pdf.rect(margin, yPosition - 5, labelWidth, rowHeight, 'S');
        pdf.rect(margin + labelWidth, yPosition - 5, valueWidth, rowHeight, 'S');
        
        // Label (bold)
        pdf.setFont(undefined, 'bold');
        pdf.text(spec[0] + ':', margin + 3, yPosition);
        
        // Value (normal)
        pdf.setFont(undefined, 'normal');
        pdf.text(spec[1], margin + labelWidth + 3, yPosition);
        
        yPosition += rowHeight;
      }
      
      yPosition += 10; // Space between equipment items
    }
  } else {
    // Old format - single equipment with table layout
    const specs = [
      ['Engine Manufacturer', offerData.engineManufacturer],
      ['Engine Type', offerData.engineType],
      ['Engine Output', offerData.engineOutput ? addUnit(offerData.engineOutput, 'KW') : ''],
      ['Year Built', offerData.yearBuilt],
      ['Working Hours', offerData.workingHours ? addUnit(offerData.workingHours, 'Hours') : ''],
      ['Alternator Type', offerData.alternatorType],
      ['Voltage', offerData.voltage ? addUnit(offerData.voltage, 'KV') : ''],
      ['Frequency', offerData.frequency ? addUnit(offerData.frequency, 'Hz') : ''],
      ['Power Rating', offerData.powerRating ? addUnit(offerData.powerRating, 'KVa') : ''],
      ['Speed', offerData.speed ? addUnit(offerData.speed, 'rpm') : '']
    ].filter(spec => spec[1]);
    
    if (specs.length > 0) {
      // Calculate total height needed
      const headerHeight = 10;
      const tableHeight = specs.length * 8;
      const totalHeight = headerHeight + tableHeight + 15;
      
      // If not enough space, start new page
      if (yPosition + totalHeight > contentHeight) {
        await addNewPage();
        yPosition = margin + 10;
      }
      
      // Blue header box
      pdf.setFillColor(43, 62, 92);
      pdf.rect(margin, yPosition, contentWidth, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Equipment Details', margin + 5, yPosition + 7);
      
      yPosition += 15;
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(11);
      
      // Table
      const labelWidth = 60;
      const valueWidth = contentWidth - labelWidth;
      
      for (let index = 0; index < specs.length; index++) {
        const spec = specs[index];
        const rowHeight = 8;
        
        // Safety check
        if (yPosition + rowHeight > contentHeight) {
          await addNewPage();
          yPosition = margin + 10;
        }
        
        // Alternating row colors
        if (index % 2 === 0) {
          pdf.setFillColor(248, 248, 248);
          pdf.rect(margin, yPosition - 5, contentWidth, rowHeight, 'F');
        }
        
        // Draw borders
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.1);
        pdf.rect(margin, yPosition - 5, labelWidth, rowHeight, 'S');
        pdf.rect(margin + labelWidth, yPosition - 5, valueWidth, rowHeight, 'S');
        
        // Label
        pdf.setFont(undefined, 'bold');
        pdf.text(spec[0] + ':', margin + 3, yPosition);
        
        // Value
        pdf.setFont(undefined, 'normal');
        pdf.text(spec[1], margin + labelWidth + 3, yPosition);
        
        yPosition += rowHeight;
      }
    }
  }

  // Page 3: Commercial Terms
  await addNewPage();
  yPosition = margin + 10;
  
  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(18);
  pdf.text('Commercial Terms', margin, yPosition);
  yPosition += 15;
  
  // Price
  pdf.setFillColor(43, 62, 92);  // Dark blue from website
  pdf.rect(margin, yPosition, contentWidth, 10, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.text('Price', margin + 5, yPosition + 7);
  
  yPosition += 15;
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  pdf.text(`Total: ${offerData.price || 'Price to be confirmed'}`, margin, yPosition);
  pdf.setFont(undefined, 'normal');
  pdf.setFontSize(12);
  pdf.text('Ex works, excluding taxes and duties', margin, yPosition + 8);
  
  yPosition += 25;
  
  // Payment Terms
  pdf.setFillColor(43, 62, 92);  // Dark blue from website
  pdf.rect(margin, yPosition, contentWidth, 10, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.text('Payment Terms', margin + 5, yPosition + 7);
  
  yPosition += 15;
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(12);
  
  // Handle payment terms as array or string
  if (Array.isArray(offerData.paymentTerms)) {
    const validTerms = offerData.paymentTerms.filter(term => term.trim());
    if (validTerms.length > 0) {
      for (const term of validTerms) {
        // Split long payment terms into multiple lines
        const bulletText = `• ${term}`;
        const wrappedLines = pdf.splitTextToSize(bulletText, contentWidth);
        
        for (let i = 0; i < wrappedLines.length; i++) {
          if (yPosition > contentHeight) {
            await addNewPage();
            yPosition = margin + 10;
          }
          
          // For continuation lines, indent instead of using bullet
          const lineText = i === 0 ? wrappedLines[i] : `  ${wrappedLines[i]}`;
          pdf.text(lineText, margin, yPosition);
          yPosition += 7;
        }
        
        // Add small spacing between different bullet points
        yPosition += 2;
      }
    } else {
      pdf.text('Payment terms to be discussed.', margin, yPosition);
      yPosition += 7;
    }
  } else {
  const paymentLines = pdf.splitTextToSize(offerData.paymentTerms || 'Payment terms to be discussed.', contentWidth);
  paymentLines.forEach(line => {
    pdf.text(line, margin, yPosition);
    yPosition += 7;
  });
  }
  
  yPosition += 15;
  
  // Delivery Time
  pdf.setFillColor(43, 62, 92);  // Dark blue from website
  pdf.rect(margin, yPosition, contentWidth, 10, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(14);
  pdf.text('Delivery Time', margin + 5, yPosition + 7);
  
  yPosition += 15;
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(12);
  pdf.text(offerData.deliveryTime || 'To be confirmed', margin, yPosition);
  
  // Additional notes if any
  const hasNotes = Array.isArray(offerData.additionalNotes) 
    ? offerData.additionalNotes.filter(note => note.trim()).length > 0
    : offerData.additionalNotes;
    
  if (hasNotes) {
    yPosition += 20;
    if (yPosition + 40 > contentHeight) {
      await addNewPage();
      yPosition = margin + 10;
    }
    
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(14);
    pdf.text('Additional Notes:', margin, yPosition);
    yPosition += 8;
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(11);
    
    // Handle additional notes as array or string
    if (Array.isArray(offerData.additionalNotes)) {
      const validNotes = offerData.additionalNotes.filter(note => note.trim());
      for (const note of validNotes) {
        if (yPosition > contentHeight) {
          await addNewPage();
          yPosition = margin + 10;
        }
        
        // Split long bullet points into multiple lines
        const bulletText = `• ${note}`;
        const wrappedLines = pdf.splitTextToSize(bulletText, contentWidth);
        
        for (let i = 0; i < wrappedLines.length; i++) {
          if (yPosition > contentHeight) {
            await addNewPage();
            yPosition = margin + 10;
          }
          
          // For continuation lines, indent instead of using bullet
          const lineText = i === 0 ? wrappedLines[i] : `  ${wrappedLines[i]}`;
          pdf.text(lineText, margin, yPosition);
          yPosition += 6;
        }
        
        // Add small spacing between different bullet points
        yPosition += 2;
      }
    } else {
    const notesLines = pdf.splitTextToSize(offerData.additionalNotes, contentWidth);
    for (const line of notesLines) {
      if (yPosition > contentHeight) {
        await addNewPage();
        yPosition = margin + 10;
      }
      pdf.text(line, margin, yPosition);
      yPosition += 6;
      }
    }
  }

  // Page 4+: Scope of Supply (only if not empty)
  if (offerData.scopeOfSupply && offerData.scopeOfSupply.length > 0) {
    await addNewPage();
    yPosition = margin + 10;
    
    pdf.setFont(undefined, 'bold');
    pdf.setFontSize(18);
    pdf.text('Scope of Supply', margin, yPosition);
    yPosition += 15;
    
    // Table header
    const tableTop = yPosition;
    const col1Width = 15;
    const col2Width = contentWidth - 45;
    const col3Width = 15;
    // col4Width is same as col3Width (15), using col3Width for unit column
    
    pdf.setFillColor(43, 62, 92);  // Dark blue from website
    pdf.rect(margin, tableTop, contentWidth, 10, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.text('No', margin + 3, tableTop + 7);
    pdf.text('Description', margin + col1Width + 3, tableTop + 7);
    pdf.text('Qty', margin + col1Width + col2Width + 3, tableTop + 7);
    pdf.text('Unit', margin + col1Width + col2Width + col3Width + 3, tableTop + 7);
    
    yPosition = tableTop + 10;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);

    // Table rows
    for (let index = 0; index < offerData.scopeOfSupply.length; index++) {
      const item = offerData.scopeOfSupply[index];
      const descLines = pdf.splitTextToSize(item.description, col2Width - 6);
      const rowHeight = Math.max(8, descLines.length * 5 + 4);
      
      // Check if we need a new page
      if (yPosition + rowHeight > contentHeight) {
        await addNewPage();
        yPosition = margin + 10;
        
        // Redraw header on new page
        pdf.setFillColor(43, 62, 92);  // Dark blue from website
        pdf.rect(margin, yPosition, contentWidth, 10, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');
        pdf.text('No', margin + 3, yPosition + 7);
        pdf.text('Description', margin + col1Width + 3, yPosition + 7);
        pdf.text('Qty', margin + col1Width + col2Width + 3, yPosition + 7);
        pdf.text('Unit', margin + col1Width + col2Width + col3Width + 3, yPosition + 7);
        yPosition += 10;
        pdf.setTextColor(0, 0, 0);
        pdf.setFont(undefined, 'normal');
      }
      
      // Row background
      if (index % 2 === 0) {
        pdf.setFillColor(248, 248, 248);
        pdf.rect(margin, yPosition, contentWidth, rowHeight, 'F');
      }
      
      // Borders
      pdf.setDrawColor(200, 200, 200);
      pdf.rect(margin, yPosition, contentWidth, rowHeight, 'S');
      
      // Content
      pdf.text((index + 1).toString(), margin + 3, yPosition + 6);
      for (let lineIndex = 0; lineIndex < descLines.length; lineIndex++) {
        pdf.text(descLines[lineIndex], margin + col1Width + 3, yPosition + 6 + (lineIndex * 5));
      }
      pdf.text(item.quantity.toString(), margin + col1Width + col2Width + 3, yPosition + 6);
      pdf.text(item.unit, margin + col1Width + col2Width + col3Width + 3, yPosition + 6);
      
      yPosition += rowHeight;
    }
  }

  // Photos Section (only if not empty)
  if (offerData.images && offerData.images.length > 0) {
    // Total byte budget for all embedded photos (~3.5 MB), leaving headroom
    // for the watermark, logo, fonts and PDF structure inside a 5 MB target.
    // Each photo gets an equal share, clamped between 120 KB and 480 KB.
    const totalPhotoBudgetBytes = 3.5 * 1024 * 1024;
    const perPhotoBudgetBytes = Math.max(
      120 * 1024,
      Math.min(480 * 1024, Math.floor(totalPhotoBudgetBytes / offerData.images.length))
    );
    
    let photoIndex = 0;
    
    // Group images by layout (1 per page or 2 per page)
    while (photoIndex < offerData.images.length) {
      // Look ahead to determine layout for this page
      const availableImages = [];
      let tempIndex = photoIndex;
      
      // Check dimensions of next few images to decide layout
      while (tempIndex < offerData.images.length && availableImages.length < 2) {
        const image = offerData.images[tempIndex];
        try {
          const dims = await getImageDimensions(image.url);
          availableImages.push({ image, dims, index: tempIndex });
        } catch (e) {
          availableImages.push({ image, dims: { width: 1, height: 1 }, index: tempIndex });
        }
        tempIndex++;
      }
      
      if (availableImages.length === 0) break;
      
      // Determine if we should use 1 or 2 images on this page
      const firstImageHeightRatio = availableImages[0].dims.height / availableImages[0].dims.width;
      const pageHeightRatio = contentHeight / contentWidth;
      
      // If image height is > 70% of page height when scaled to fit width, use 1 per page
      const useOnePerPage = firstImageHeightRatio > (0.7 * pageHeightRatio);
      
      await addNewPage();
      yPosition = margin + 10;
      
      // Page header for photos
      pdf.setFont(undefined, 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${offerData.plantCapacity || ''} ${offerData.engineType || 'Power Plant'} - Images`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      const photosOnThisPage = useOnePerPage ? 1 : Math.min(2, availableImages.length);
      const spacing = photosOnThisPage === 1 ? 0 : 10; // mm between images
      const availableHeight = contentHeight - yPosition - 20;
      const maxPhotoHeight = photosOnThisPage === 1 ? availableHeight : (availableHeight - spacing) / 2;
      const maxPhotoWidth = contentWidth * 0.95;
      
      for (let i = 0; i < photosOnThisPage; i++) {
        if (photoIndex >= offerData.images.length) break;
        
        const image = offerData.images[photoIndex];
        const photoY = yPosition + i * (maxPhotoHeight + spacing);
        
        try {
          // Get resized image (NO ROTATION). 150 DPI at A4 content width
          // (~1000 px wide) is fully sharp on screen and in office printing.
          const pxW = mmToPx(maxPhotoWidth, 150);
          const pxH = mmToPx(maxPhotoHeight, 150);
          const dataUrl = await getResizedDataUrl(image.url, pxW, pxH, perPhotoBudgetBytes);

          // Maintain aspect: compute final width/height to fit box
          const imgTmp = new Image();
          imgTmp.src = dataUrl;
          await new Promise((r) => { imgTmp.onload = r; imgTmp.onerror = r; });
          const imgRatio = imgTmp.width / imgTmp.height;
          const boxRatio = maxPhotoWidth / maxPhotoHeight;
          let drawW = maxPhotoWidth;
          let drawH = maxPhotoHeight;
          if (imgRatio > boxRatio) {
            // image wider → limit by width
            drawH = maxPhotoWidth / imgRatio;
          } else {
            // image taller → limit by height
            drawW = maxPhotoHeight * imgRatio;
          }
          const photoX = margin + (contentWidth - drawW) / 2;
          
          // Add image with high quality
          pdf.addImage(dataUrl, 'JPEG', photoX, photoY, drawW, drawH, undefined, 'FAST');
          
          // Add image caption if available
          if (image.caption) {
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(image.caption, pageWidth / 2, photoY + drawH + 5, { align: 'center' });
          }
        } catch (error) {
          // If image fails to load, show placeholder
          pdf.setFillColor(240, 240, 240);
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(margin + (contentWidth - maxPhotoWidth) / 2, photoY, maxPhotoWidth, maxPhotoHeight, 'FD');
          pdf.setFontSize(12);
          pdf.setTextColor(100, 100, 100);
          pdf.text('Image could not be loaded', pageWidth / 2, photoY + maxPhotoHeight / 2, { align: 'center' });
        }
        
        photoIndex++;
      }
    }
  }

  // Add page numbers to all pages
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  }

  // Save the PDF with specific naming format
  // Format: [XX] Megawatt [Manufacturer] [Engine Type] [Fuel Type] Power Plant Offer to [Client Name] - [Quotation Number].pdf
  const capacity = offerData.plantCapacity ? offerData.plantCapacity.replace(/\s*MW\s*$/i, '') : 'XX';
  
  // Get manufacturer and engine type from first equipment if using new format
  let manufacturer, engineType;
  if (offerData.equipment && Array.isArray(offerData.equipment) && offerData.equipment.length > 0) {
    const firstEquipment = offerData.equipment[0];
    manufacturer = firstEquipment.engineManufacturer || offerData.plantTitle || 'Unknown';
    engineType = firstEquipment.engineType || firstEquipment.equipmentType || 'Power Plant';
  } else {
    // Old format - use direct fields
    manufacturer = offerData.engineManufacturer || 'Unknown';
    engineType = offerData.engineType || 'Unknown';
  }
  
  const fuelType = offerData.fuelType || 'Gas';
  const clientName = offerData.clientName || 'Client';
  const quotationNo = offerData.quotationNo || 'DRAFT';
  
  const fileName = `${capacity} Megawatt ${manufacturer} ${engineType} ${fuelType} Power Plant Offer to ${clientName} - ${quotationNo}.pdf`;
  
  const pdfSizeMB = pdf.output('blob').size / (1024 * 1024);
  console.log(`Generated PDF size: ${pdfSizeMB.toFixed(2)} MB`);
  if (pdfSizeMB > 5) {
    console.warn(`PDF exceeds the 5 MB target (${pdfSizeMB.toFixed(2)} MB) — check for oversized watermark/logo sources.`);
  }
  
  pdf.save(fileName);
  
  return true;
}; 